import React, { useMemo } from 'react';
import { BarChart } from '../ui/chart-wrapper';
import DataTable from './DataTable';
import WidgetBase from './WidgetBase';
import { useWidgetData } from '../../hooks/useWidgetData';
import { exportToCSV, exportChartDataToCSV } from '../../utils/exportToCSV';

/**
 * Bar Widget Component
 * Modernized replacement for BarList.js
 * Displays data as bar chart or table
 */
function BarWidget({ data, handlechangea }) {
    // Fetch data using custom hook
    const { data: apiData, loading, error } = useWidgetData(
        data.url,
        data.RequestJson,
        data.RequestJson1
    );

    // Transform API data to Chart.js format
    const chartData = useMemo(() => {
        if (!apiData || apiData.length === 0) return null;

        try {
            const dataset = apiData[0].columnDataSet1?.[0];
            if (!dataset || !dataset.labels || !dataset.data) return null;

            return {
                labels: dataset.labels,
                datasets: [{
                    label: data.YlabelString || 'Value',
                    data: dataset.data,
                    backgroundColor: data.ColorCode || '#f19f57',
                    borderColor: data.ColorCode || '#f19f57',
                    borderWidth: 1,
                    hoverBackgroundColor: '#FF8B6C'
                }]
            };
        } catch (err) {
            console.error('Error transforming chart data:', err);
            return null;
        }
    }, [apiData, data]);

    // Transform API data to table format
    const tableData = useMemo(() => {
        if (!chartData) return [];

        return chartData.labels.map((label, index) => ({
            label: label,
            value: chartData.datasets[0].data[index]
        }));
    }, [chartData]);

    // Table columns configuration
    const columns = [
        {
            key: 'label',
            label: data.XlabelString || 'Label',
            sortable: true
        },
        {
            key: 'value',
            label: data.YlabelString || 'Value',
            sortable: true,
            format: (val) => typeof val === 'number' ? val.toLocaleString() : val
        }
    ];

    // Handle row click for drill-down (if applicable)
    const handleRowClick = (row) => {
        if (handlechangea && data.DataType === 'GroupNumber') {
            // Trigger filter change for group number drill-down
            handlechangea({ target: { value: row.label } });
        }
    };

    // Export handler
    const handleExport = () => {
        if (chartData) {
            exportChartDataToCSV(chartData, data.title || 'chart-data');
        } else if (tableData.length > 0) {
            exportToCSV(tableData, data.title || 'table-data', columns);
        }
    };

    // Chart options
    const chartOptions = {
        onClick: (event, elements) => {
            if (elements.length > 0 && handlechangea) {
                const index = elements[0].index;
                const label = chartData.labels[index];
                if (data.DataType === 'GroupNumber') {
                    handlechangea({ target: { value: label } });
                }
            }
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: data.XlabelString || ''
                }
            },
            y: {
                title: {
                    display: true,
                    text: data.YlabelString || ''
                },
                beginAtZero: true
            }
        }
    };

    return (
        <WidgetBase
            loading={loading}
            error={error}
            chartComponent={
                chartData ? (
                    <BarChart
                        data={chartData}
                        options={chartOptions}
                        height={350}
                    />
                ) : null
            }
            tableComponent={
                tableData.length > 0 ? (
                    <DataTable
                        columns={columns}
                        data={tableData}
                        onRowClick={data.DataType === 'GroupNumber' ? handleRowClick : null}
                        pagination={{ enabled: true, itemsPerPage: 10 }}
                        sortable={true}
                        searchable={true}
                    />
                ) : null
            }
            onExport={handleExport}
            showToggle={true}
            showExport={true}
        />
    );
}

export default BarWidget;
